import { and, not } from 'tio-ui/utilities';
import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import Popover from 'tio-ui/components/popover';
import formatCentsToDollars from 'tio-common/helpers/format-cents-to-dollars';
import AlertBanner from 'tio-ui/components/alert-banner';
import {
  PhDotsThreeVertical,
  PhGraduationCap,
  PhPlus,
  PhNotePencil,
  PhTrash,
} from 'ember-phosphor-icons';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';

interface StepExpenseItemsSignature {
  Args: {
    stepNumber: number;
    application: TasApplicationModel;
    expenseTypeField: string;
    onOpenEducationDrawer: (course: TasCourseModel | null) => void;
    onToggleDeleteCourse: (course: TasCourseModel | null) => void;
    onNext: () => void;
    hasAttempted: boolean;
    isComplete: boolean;
    /**
     * Over-limit sentence from the wizard's benefit-limit gate, or null within
     * limits. Warns here — where the total is actually edited — but never blocks
     * Next: the hard gate stays on the submit step, and a scholarship added on
     * the later payment step can clear this without touching a course.
     */
    overLimitWarning: string | null;
  };
}

export default class StepExpenseItems extends Component<StepExpenseItemsSignature> {
  @tracked openMenuCourseId: string | null = null;

  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get hasExpenseItems() {
    return (this.args.application.activeCourses?.length ?? 0) > 0;
  }

  get totalCents() {
    return (
      this.args.application.activeCourses?.reduce(
        (sum, course) => sum + (course.courseTotal ?? 0),
        0
      ) ?? 0
    );
  }

  toggleMenu = (courseId: string | null) => {
    this.openMenuCourseId = this.openMenuCourseId === courseId ? null : courseId;
  };

  closeMenu = () => {
    this.openMenuCourseId = null;
  };

  handleEdit = (course: TasCourseModel) => {
    this.openMenuCourseId = null;
    this.args.onOpenEducationDrawer(course);
  };

  handleDelete = (course: TasCourseModel) => {
    this.openMenuCourseId = null;
    this.args.onToggleDeleteCourse(course);
  };

  <template>
    <Section>
      <:header>
        <div class="tio-row-between w-full">
          <div class="tio-row-tight">
            <span class="tio-guided-step-badge">{{this.displayNumber}}</span>
            <span>{{t "common.add"}}
              {{@expenseTypeField}}<span class="tio-fg-danger ml-0.5">*</span></span>
          </div>
          {{#if this.hasExpenseItems}}
            <span class="font-semibold tio-fg-primary">
              {{t "tas.guided.steps.total"}}
              {{formatCentsToDollars this.totalCents}}
            </span>
          {{/if}}
        </div>
      </:header>
      <:body>
        <p class="tio-fg-muted mb-8">{{t "tas.guided.steps.expense_items_description"}}</p>

        {{#if (and @hasAttempted (not this.hasExpenseItems))}}
          <AlertBanner @intent="warning" class="mb-4">
            <p class="tio-text-sm-medium">{{t "tas.guided.steps.expense_items_required"}}</p>
          </AlertBanner>
        {{/if}}

        {{#if @overLimitWarning}}
          <AlertBanner @intent="warning" class="mb-4" data-test-expense-over-limit>
            <p class="tio-text-sm-medium">{{@overLimitWarning}}</p>
          </AlertBanner>
        {{/if}}

        {{#if this.hasExpenseItems}}
          <div class="tio-col">
            {{#each @application.activeCourses as |course|}}
              <div class="tio-guided-expense-item">
                <div class="tio-row-loose">
                  <div class="tio-bg-sunken rounded-full w-6 h-6 tio-row-center shrink-0">
                    <PhGraduationCap @size="18" class="tio-fg-muted" />
                  </div>
                  <span class="font-medium tio-fg-primary">{{course.courseName}}</span>
                </div>
                <div class="tio-row-loose">
                  <span class="font-semibold tio-fg-primary">
                    {{#if @application.isCreditBased}}
                      {{course.courseCredit}}
                      {{t "common.credits"}}
                    {{else}}
                      {{formatCentsToDollars course.courseTotal}}
                    {{/if}}
                  </span>
                  <Popover
                    @placement="bottom-end"
                    @isOpen={{eq this.openMenuCourseId course.id}}
                    @onClose={{this.closeMenu}}
                    @maxWidth={{180}}
                  >
                    <:trigger as |pop|>
                      <button
                        type="button"
                        data-test-edit-education-drawer
                        class="tio-fg-disabled hover:tio-fg-secondary"
                        {{pop.register}}
                        {{on "click" (fn this.toggleMenu course.id)}}
                      >
                        <PhDotsThreeVertical @size="20" />
                      </button>
                    </:trigger>
                    <:body>
                      <div class="tio-col-flush py-1">
                        <button
                          type="button"
                          class="tio-menu-action tio-menu-action--compact"
                          {{on "click" (fn this.handleEdit course)}}
                        >
                          <PhNotePencil @size="16" />
                          {{t "common.edit"}}
                        </button>
                        <button
                          type="button"
                          class="tio-row-tight w-full px-3 py-2 text-sm tio-fg-danger hover:tio-bg-danger-soft-hover rounded"
                          {{on "click" (fn this.handleDelete course)}}
                        >
                          <PhTrash @size="16" />
                          {{t "common.delete"}}
                        </button>
                      </div>
                    </:body>
                  </Popover>
                </div>
              </div>
            {{/each}}

            <button
              type="button"
              class="tio-guided-expense-add"
              data-test-add-education-drawer
              {{on "click" (fn @onOpenEducationDrawer null)}}
            >
              <PhPlus @size="18" />
              {{t "tas.guided.steps.add_item"}}
            </button>
          </div>
        {{else}}
          <div class="tio-guided-expense-empty">
            <PhGraduationCap @size="32" class="tio-fg-muted" />
            <div class="tio-stack-center gap-1 pb-2">
              <span class="tio-text-lg-semibold tio-fg-primary">{{t
                  "tas.guided.steps.no_expense_items"
                }}</span>
              <span class="font-medium tio-fg-muted text-center max-w-86">{{t
                  "tas.guided.steps.no_expense_items_description"
                }}</span>
            </div>
            <Button
              @intent="primary"
              data-test-add-education-drawer
              {{on "click" (fn @onOpenEducationDrawer null)}}
            >
              {{t "common.add"}}
              {{@expenseTypeField}}
            </Button>
          </div>
        {{/if}}

        <div class="flex pt-8">
          <Button @intent="primary" data-test-next-step {{on "click" @onNext}}>
            {{t "common.next"}}
          </Button>
        </div>
      </:body>
    </Section>
  </template>
}
